import React from 'react';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import { GIButton } from 'app-components';

import { CONSENT_DECISION } from './constants';
import { useCookieConsent } from './useCookieConsent';

const BannerRoot = styled.section`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1400;
    background-color: ${({ theme }) => theme.primaryDarkest};
    color: ${({ theme }) => theme.defaultWhite};
    padding: 16px 24px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.2);
    line-height: 1.5;

    @media (max-width: 768px) {
        padding: 24px 20px 22px;
    }
`;

const Inner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 24px;

    @media (max-width: 768px) {
        max-width: none;
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
    }
`;

const Body = styled.p`
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    color: ${({ theme }) => theme.defaultWhite};
    font-size: 14.96px;

    @media (max-width: 768px) {
        font-size: 14.96px;
        line-height: 1.55;
    }
`;

const Actions = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    flex-shrink: 0;

    .gi-button {
        margin: 0;
        padding: 6px 12px;
    }

    @media (max-width: 768px) {
        width: 100%;

        .gi-button {
            flex: 1 1 auto;
            min-height: 42px;
        }
    }
`;

export const CookieConsentBanner: React.FC = () => {
    const { decision, accept, deny } = useCookieConsent();
    const intl = useIntl();
    const theme = useTheme();
    const isThemeReady = Boolean(theme.primaryDarkest && theme.defaultWhite);

    if (decision !== CONSENT_DECISION.UNDECIDED || !isThemeReady) return null;

    return (
        <BannerRoot
            role="region"
            aria-label={intl.formatMessage({ id: 'cookieConsent.banner.ariaLabel.region' })}
            data-testid="cookie-consent-banner"
        >
            <Inner>
                <Body>
                    {intl.formatMessage({ id: 'cookieConsent.banner.msg.body' })}
                </Body>
                <Actions>
                    <GIButton
                        id="cookie-consent-decline"
                        type="button"
                        labelId="cookieConsent.banner.btn.decline"
                        variant="secondary"
                        ariaLabel={intl.formatMessage({ id: 'cookieConsent.banner.btn.decline' })}
                        onClickHandler={deny}
                        dataTestId="cookie-consent-decline"
                    />
                    <GIButton
                        id="cookie-consent-accept"
                        type="button"
                        labelId="cookieConsent.banner.btn.accept"
                        variant="secondary"
                        ariaLabel={intl.formatMessage({ id: 'cookieConsent.banner.btn.accept' })}
                        onClickHandler={accept}
                        dataTestId="cookie-consent-accept"
                    />
                </Actions>
            </Inner>
        </BannerRoot>
    );
};
